import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { AvatarEditor } from "../components/AvatarEditor";
import { clearProfile, updateAvatar, updateName } from "../local/profile";
import { loadGameSettings, saveGameSettings, type MovementMode } from "../local/gameSettings";
import { sfx } from "../game/audio";

export function LocalSettings() {
  const { profile, setProfile } = useLocalProfile();
  const nav = useNavigate();
  const [gameSettings, setGameSettings] = useState(() => loadGameSettings());
  const [name, setName] = useState(profile?.displayName ?? "");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }

  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === profile.displayName) return;
    setProfile(updateName(profile, trimmed));
    setSavedAt(Date.now());
  };
  const saveAvatar = (cfg: Parameters<typeof updateAvatar>[1]) => {
    setProfile(updateAvatar(profile, cfg));
    setSavedAt(Date.now());
  };
  const reset = () => {
    if (!confirm("Reset character? You'll lose your coins, upgrades, and progress.")) return;
    clearProfile();
    setProfile(null);
    nav("/", { replace: true });
  };
  const saveMovementMode = (mode: MovementMode) => {
    const next = { ...gameSettings, movementMode: mode };
    setGameSettings(next);
    saveGameSettings(next);
    sfx.menu();
    setSavedAt(Date.now());
  };
  const saveVolume = (patch: Partial<typeof gameSettings>) => {
    const next = { ...gameSettings, ...patch };
    setGameSettings(next);
    saveGameSettings(next);
    if (patch.audioVolume !== undefined) sfx.menu();
    setSavedAt(Date.now());
  };

  return (
    <div className="home-dashboard settings-page">
      <div className="settings-topbar">
        <button className="home-settings-btn" onClick={() => nav("/")} aria-label="Menu">←</button>
        <h1>Settings</h1>
        {savedAt && (
          <span className="settings-saved">
            Saved {new Date(savedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      <div className="settings-grid">
      <div className="settings-panel stack">
        <h2>Name</h2>
        <div className="row" style={{ gap: 8 }}>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={20}
            style={{ flex: 1 }}
          />
          <button className="btn btn-blue" onClick={saveName}>Save</button>
        </div>
      </div>

      <div className="settings-panel stack settings-look">
        <h2>Look</h2>
        <AvatarEditor initial={profile.avatarConfig} onSaved={saveAvatar} localOnly />
      </div>

      <div className="settings-panel stack">
        <h2>Controls</h2>
        <div className="segmented">
          <button
            className={gameSettings.movementMode === "free" ? "active" : ""}
            onClick={() => saveMovementMode("free")}
          >
            Free
          </button>
          <button
            className={gameSettings.movementMode === "lanes" ? "active" : ""}
            onClick={() => saveMovementMode("lanes")}
          >
            Lanes
          </button>
        </div>
        <label className="settings-slider">
          <span>Movement sensitivity</span>
          <strong>{Math.round(gameSettings.movementSensitivity * 100)}%</strong>
          <input
            type="range"
            min="25"
            max="150"
            value={Math.round(gameSettings.movementSensitivity * 100)}
            onChange={(e) => saveVolume({ movementSensitivity: Number(e.target.value) / 100 })}
          />
        </label>
        <label className="settings-slider">
          <span>Controller deadzone</span>
          <strong>{Math.round(gameSettings.controllerDeadzone * 100)}%</strong>
          <input
            type="range"
            min="5"
            max="40"
            value={Math.round(gameSettings.controllerDeadzone * 100)}
            onChange={(e) => saveVolume({ controllerDeadzone: Number(e.target.value) / 100 })}
          />
        </label>
        <label className="settings-slider">
          <span>Camera sensitivity</span>
          <strong>{Math.round(gameSettings.cameraSensitivity * 100)}%</strong>
          <input
            type="range"
            min="25"
            max="150"
            value={Math.round(gameSettings.cameraSensitivity * 100)}
            onChange={(e) => saveVolume({ cameraSensitivity: Number(e.target.value) / 100 })}
          />
        </label>
      </div>

      <div className="settings-panel stack">
        <h2>Audio</h2>
        <label className="settings-slider">
          <span>Music</span>
          <strong>{Math.round(gameSettings.musicVolume * 100)}%</strong>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(gameSettings.musicVolume * 100)}
            onChange={(e) => saveVolume({ musicVolume: Number(e.target.value) / 100 })}
          />
        </label>
        <label className="settings-slider">
          <span>Voice and SFX</span>
          <strong>{Math.round(gameSettings.audioVolume * 100)}%</strong>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(gameSettings.audioVolume * 100)}
            onChange={(e) => saveVolume({ audioVolume: Number(e.target.value) / 100 })}
          />
        </label>
      </div>

      <div className="settings-panel stack">
        <h2>About</h2>
        <p className="t-body t-muted" style={{ margin: 0 }}>
          Word Runner is a tablet-first runner game for kids learning English.
        </p>
        <p className="t-caption t-muted" style={{ textTransform: "none", margin: 0 }}>
          Pictures by{" "}
          <a href="https://openmoji.org" target="_blank" rel="noreferrer">
            OpenMoji
          </a>{" "}
          (CC BY-SA 4.0). Voice by Mistral AI.
        </p>
      </div>

      <div className="settings-panel stack">
        <h2>Reset</h2>
        <p className="t-body t-muted" style={{ margin: 0 }}>
          Wipes coins, upgrades, progress, and avatar.
        </p>
        <button className="btn btn-red btn-block" onClick={reset}>
          Reset character
        </button>
      </div>
      </div>
    </div>
  );
}
